import { PaymentStatus } from '@wix/ambassador-premium-data-view-retriever-server/types';
import { AccountBrandingInfo } from '@wix/ambassador-premium-reseller-mgmt/types';
import type { ApiV1Cycle } from '@wix/ambassador-premium-store/types';
import { VouchersType } from '@wix/ambassador-premium-vouchers/types';
import { ExperimentsProviderOptions } from '@wix/wix-experiments-react';
import {
  Country,
  CurrentPlanInOfferingMode,
  Experiments,
  Language,
  PackagePickerType,
  PaymentCycle,
  PlanType,
  ProductChangeType,
  ProductFamily,
  PurchaseMode,
  RealEstateId,
  SEPA_ENABLED_COUNTRIES,
  Vertical,
  AppFlow,
} from 'enums';
import { action, computed, observable } from 'mobx';
import { AdvancedPlansProductModel } from 'models/AdvancedPlansProductModel';
import {
  BiPlan,
  BiStepNames,
  PaymentCycle_BIMap,
  PlanTypeClick,
} from 'services/BiLogger/BiLoggerTypes';
import { SentryLogger } from 'services/SentryLogger';
import { IStorage } from 'services/Storage/Storage';
import { Utils } from 'services/utils';
import {
  ActionTypeReporter,
  AdvancedPlansProductData,
  BaseProduct,
  ChangePlanProductProperties,
  IAppContext,
  ParsedAppIntentElements,
  ProductData,
  RecommendedHatData,
  VoucherSettings,
} from 'types';
import { isSunriseExperimentsOn, throttle } from 'utils';
import {
  plansPageTabs,
  taxIncludedCountries,
  WEBSITE_VERTICALS,
} from '../constants';
import { isRedirectToCartFlow } from '../utils/flows';
import { SUNRISE_FAMILY_LIGHT_ID } from '../../src/server/renderers/common/constants';

export const APP_STEPS = [
  'app.steps.step1',
  'app.steps.step2',
  'app.steps.step3',
];

export const ADVANCED_PLANS_APP_STEPS = [
  'Advanced.Plans_Steps_Step1',
  'Advanced.Plans_Steps_Step2',
  'Advanced.Plans_Steps_Step3',
];

export enum STEP_ACTIONS {
  INCREMENT = 'increment',
  DECREMENT = 'decrement',
  SPECIFIC = 'specific',
}

export enum TableViewMode {
  Default = 'default',
  Medium = 'medium',
}

export class AppStore {
  @observable currentStep = 0;
  @observable previousStep = 0;
  @observable currentProduct: ProductData | AdvancedPlansProductData;
  @observable scrollToTopRef;
  @observable changePlanModalOpened = false;
  @observable planType = PlanType.Website;
  @observable currentCycle = PaymentCycle.YEARLY;
  @observable alternativeCycle: ApiV1Cycle | undefined = undefined;
  @observable isNoWebsiteModalOpened = false;
  @observable isWizardHidden = false;
  @observable tableViewMode: TableViewMode = TableViewMode.Default;

  baseUrl = '/';
  isEditorX: boolean = false;
  isDayful: boolean = false;
  isMobile: boolean = false;
  couponCode?: string;
  experiments: ExperimentsProviderOptions['experiments'];
  storage: IStorage;

  constructor(
    public appContext: IAppContext,
    private readonly changeStepThrottle = 700,
  ) {
    this.experiments = appContext.experiments;
    this.storage = appContext.storage;
    this.isEditorX =
      this.appContext.packagePickerType === PackagePickerType.EditorX;
    this.isDayful =
      this.appContext.packagePickerType === PackagePickerType.Dayful;
    this.planType = this.getLandingTab();

    this.isMobile = this.appContext.isMobile;
    if (!appContext.debug) {
      window.addEventListener('beforeunload', this.handlePageUnload);
    }

    this.currentProduct = this.appContext.advancedPlansProducts?.[0];
  }

  private readonly handlePageUnload = (e: Event) => {
    const confirmationMessage = '';
    e.preventDefault();
    e.returnValue = true; // Gecko, Trident, Chrome 34+
    return confirmationMessage; // Gecko, WebKit, Chrome <34
  };

  removeUnloadListener = () => {
    window.removeEventListener('beforeunload', this.handlePageUnload);
  };

  @action.bound
  private incrementStep() {
    return this.currentStep++;
  }

  @action.bound
  private decrementStep() {
    return this.currentStep--;
  }

  @action.bound
  setTableViewMode(mode: TableViewMode) {
    this.tableViewMode = mode;
  }

  @action.bound
  setScrollToTopRef(ref: any) {
    this.scrollToTopRef = ref;
  }

  @action.bound
  setStep(step: number) {
    this.currentStep = step;
  }

  @action.bound
  setPlanType(planType: PlanType) {
    this.planType = planType;
  }

  @action.bound
  openChangePlanModal() {
    this.changePlanModalOpened = true;
  }

  @action.bound
  closeChangePlanModal() {
    this.changePlanModalOpened = false;
  }

  @action.bound
  activateDirectChecktOut() {
    this.hideStepWizard();
    this.setStep(2);
  }

  @action.bound
  hideStepWizard() {
    this.isWizardHidden = true;
  }

  private notifyBIOnCycleChange() {
    this.appContext.biLogger &&
      this.appContext.biLogger.logSelectCycleBiEvent({
        cycleId: this.currentCycle,
        productId: this.currentProduct.id,
        productGuid: this.currentProduct.guid,
        cycle: PaymentCycle_BIMap[this.currentCycle],
        cartFlow: this.appFlow,
      });
  }

  @action
  handleOnBackButtonClick = () => {
    const actionReporter = (from, to) => {
      this.appContext.biLogger.logStepBackBiEvent({
        fromStep: `change_${to}_from_${from}_back`,
        productId: this.currentProduct.id,
        productGuid: this.currentProduct.guid,

        cycle: PaymentCycle_BIMap[this.currentCycle],
      });
    };
    if (this.isDowngradePlanOfDeclineChargeService) {
      this.changeStep(STEP_ACTIONS.SPECIFIC, actionReporter, 0);
    } else {
      this.changeStep(STEP_ACTIONS.DECREMENT, actionReporter);
    }
  };
  @action
  selectTab = (tab: PlanType, clickType: PlanTypeClick = PlanTypeClick.Top) => {
    this.setPlanType(tab);

    const pricing = this.productsPrices;
    this.appContext.biLogger.logChangeTabBiEvent({
      clickType,
      ppType: tab,
      pricing,
      currency: this.appContext.currencyCode,
    });
  };

  @action
  jumpToTab = (goToPlan: PlanType) => {
    this.selectTab(plansPageTabs[plansPageTabs.indexOf(goToPlan)]);
  };

  @action.bound
  setCurrentCycle({
    cycle,
    sendBiEvent = true,
  }: {
    cycle: PaymentCycle;
    sendBiEvent?: boolean;
  }) {
    this.currentCycle = cycle;
    sendBiEvent && this.notifyBIOnCycleChange();
  }

  @action.bound
  setAlternativeCycle(alternativeCycle?: ApiV1Cycle) {
    this.alternativeCycle = alternativeCycle;
  }

  @action.bound
  setCouponCode(couponCode: string) {
    this.couponCode = couponCode;
  }

  @action
  setNoWebsiteModal = (val: boolean) => {
    this.isNoWebsiteModalOpened = val;
  };

  scrollToTop = () => {
    this.scrollToTopRef &&
      this.scrollToTopRef.scrollIntoView &&
      this.scrollToTopRef.scrollIntoView({ behavior: 'smooth' });
  };

  @action.bound
  setCurrentProduct(product: ProductData | AdvancedPlansProductData) {
    this.currentProduct = product;
  }

  setDefaultCycle(product: ProductData | AdvancedPlansProductData) {
    const enabledCycles = product.cycles
      .filter((cycle) => !cycle.isCycleDisabledInProduct)
      .map((cycle) => cycle.cycleId);

    if (
      this.isCycleSelectorsInProductsPageEnabled() &&
      this.isInSale &&
      enabledCycles.includes(PaymentCycle.YEARLY)
    ) {
      this.setCurrentCycle({ cycle: PaymentCycle.YEARLY, sendBiEvent: false });
    } else if (
      this.experiments[Experiments.PersonalSaleOneYearDefault] === 'true' &&
      this.isInPersonalSale &&
      enabledCycles.includes(PaymentCycle.YEARLY)
    ) {
      this.setCurrentCycle({ cycle: PaymentCycle.YEARLY, sendBiEvent: false });
    } else if (
      this.experiments[Experiments.DefaultTreeYearsCycle] === 'true' &&
      this.isInSale &&
      enabledCycles.includes(PaymentCycle.YEAR3)
    ) {
      this.setCurrentCycle({ cycle: PaymentCycle.YEAR3, sendBiEvent: false });
    } else if (
      this.experiments[Experiments.DefaultTwoYearsCycle] === 'true' &&
      this.isInSale &&
      enabledCycles.includes(PaymentCycle.YEAR2)
    ) {
      this.setCurrentCycle({ cycle: PaymentCycle.YEAR2, sendBiEvent: false });
    } else if (enabledCycles.includes(PaymentCycle.YEARLY)) {
      this.setCurrentCycle({ cycle: PaymentCycle.YEARLY, sendBiEvent: false });
    } else {
      this.setCurrentCycle({
        cycle: enabledCycles[enabledCycles.length - 1],
        sendBiEvent: false,
      });
    }
  }

  @action.bound
  setCurrentProductWithDefaultCycle(
    product: ProductData | AdvancedPlansProductData,
  ) {
    this.setCurrentProduct(product);
    this.setDefaultCycle(product);
  }

  @computed
  get isBusinessVertical() {
    return (
      WEBSITE_VERTICALS.indexOf(Vertical[this.appContext.verticalName]) === -1
    );
  }

  @computed
  get isBusinessTab() {
    const { requestParamsMap, currentPlanDetails, advancedPlansProducts } =
      this.appContext;

    if (
      this.currentPlanInOfferingMode ===
      CurrentPlanInOfferingMode.ProductFullMatch
    ) {
      return currentPlanDetails!.type === PlanType.Business;
    }

    const isChangePlanBusiness =
      this.isChangePlan && currentPlanDetails!.type === PlanType.Business;

    const _products: BaseProduct[] = advancedPlansProducts;

    return (
      isChangePlanBusiness ||
      (!this.isUseDealerIntegrationIntentElementsEnabled &&
        this.isBusinessVertical) ||
      requestParamsMap.businessTab === 'true' ||
      _products.filter(
        (product) =>
          product.type === PlanType.Website && product.hasPurchasableCycles,
      ).length === 0
    );
  }

  @computed
  get productsPrices() {
    const { advancedPlansProducts } = this.appContext;
    const prices = advancedPlansProducts
      .filter((product) => product.type === this.planType)
      .map((product) => {
        const yearlyCycle = product.cycles.find(
          (cycle) => cycle.cycleId === PaymentCycle.YEARLY,
        );
        const monthlyActualPrice = yearlyCycle?.monthlyActualPrice ?? NaN;
        const monthlyFullPrice = yearlyCycle?.monthlyFullPrice ?? NaN;
        const biData: BiPlan = {
          price: monthlyActualPrice,
          subtitle: `premium_plan.${product.productCode}.description`,
          title: product.productName,
          id: product?.guid ?? product.id.toString(),
          discount: monthlyFullPrice - monthlyActualPrice,
        };
        return biData;
      });
    prices.sort((prod1, prod2) => prod2.price - prod1.price);
    return prices;
  }

  @computed
  get isInSale(): boolean {
    return (
      this.isInSeasonalSale || this.isInPersonalSale || this.isInPermanentSale
    );
  }

  @computed
  get isInPermanentSale(): boolean {
    const isPermanentSaleSpecEnabled =
      this.experiments[Experiments.PermanentSaleSpec] === 'true';

    if (!this.appContext.currentPlanDetails?.guid) {
      return false;
    }

    if (!this.isEligibleForSale || !isPermanentSaleSpecEnabled) {
      return false;
    }

    return this.isSeasonalPurchaseMode();
  }

  @computed
  get isEligibleForSale(): boolean {
    if (
      this.isReplaceChangePlanPurchaseModeEnabledAndIsChangePlan &&
      this.experiments[Experiments.UnifiedChangePlanPhase1] !== 'true'
    ) {
      return false;
    }
    // should show sale for unified change plan only in sunrise package picker
    const shouldShowStudioSaleInChangePlan =
      this.experiments[Experiments.changePlanStudioSale] === 'true';
    const isStudio = this.shouldRenderWixStudioPackagePicker;
    if (
      this.isReplaceChangePlanPurchaseModeEnabledAndIsChangePlan &&
      this.experiments[Experiments.UnifiedChangePlanPhase1] === 'true' &&
      (this.appContext.packagePickerType === PackagePickerType.Sandbox ||
        (isStudio && !shouldShowStudioSaleInChangePlan) ||
        !this.isSunriseV2Enabled)
    ) {
      return false;
    }
    return true;
  }

  @computed
  get isInPersonalSale(): boolean {
    if (!this.isEligibleForSale) {
      return false;
    }
    return [
      PurchaseMode.PERSONAL_SALE_AGE_1,
      PurchaseMode.PERSONAL_SALE_AGE_2,
      PurchaseMode.PERSONAL_SALE_AGE_3,
    ].includes(this.appContext.purchaseMode);
  }

  @computed
  get isInSeasonalSale(): boolean {
    if (!this.isEligibleForSale) {
      return false;
    }

    if (this.isInPermanentSale) {
      return false;
    }

    return this.isSeasonalPurchaseMode();
  }

  @computed
  get isSecondUpgrade() {
    return this.appContext.isUpgrade && !this.isChangePlan;
  }

  @computed
  get isDowngradePlanOfDeclineChargeService() {
    return (
      this.isChangePlan &&
      this.experiments[
      Experiments.AddCheckoutForDowngradeDeclinedChangePlan
      ] === 'true' &&
      this.appContext.paymentStatus === PaymentStatus.CHARGE_FAILURE &&
      this.currentProduct.productChangeType === ProductChangeType.Downgrade
    );
  }

  @computed
  get shouldStoreIntentNoTabs() {
    return (
      this.experiments[Experiments.StoreIntentNoTabs] === 'true' &&
      this.getLandingTab() === PlanType.Business &&
      this.intent === Vertical.Stores
    );
  }

  @computed
  get shouldShowTabs() {
    return !this.isDayful && !this.shouldStoreIntentNoTabs;
  }

  @computed
  get isAnonymous() {
    return this.appContext.isAnonymous;
  }

  @computed
  get isStorefrontReferrerWithDomain() {
    const isStorefrontShowDomainMobileTitleEnabled =
      this.experiments[Experiments.StorefrontShowDomainMobileTitle] === 'true';

    if (!isStorefrontShowDomainMobileTitleEnabled) {
      return false;
    }

    const { requestParamsMap } = this.appContext;
    return (
      requestParamsMap?.referralAdditionalInfo ===
      'add-domain-purchase-intent-storefront' &&
      requestParamsMap?.showDomain === 'true' &&
      requestParamsMap?.referrer === 'storefront'
    );
  }

  @computed
  get isSaleProduct() {
    const product = new AdvancedPlansProductModel(
      this.currentProduct as AdvancedPlansProductData,
      this,
    );
    return this.isInSale && product.isProductInSale;
  }

  @computed
  get discountPercent() {
    return this.isSaleProduct ? this.appContext.saleParams!.discountPercent : 0;
  }

  @computed
  get isEnterprise(): boolean {
    return this.currentProduct.productFamily === ProductFamily.Enterprise;
  }

  @computed
  get isChangePlan(): boolean {
    const { purchaseMode, currentPlanDetails, isChangePlan, experiments } =
      this.appContext;
    if (
      !currentPlanDetails ||
      currentPlanDetails.productFamily === ProductFamily.Enterprise
    ) {
      return false;
    }
    if (experiments[Experiments.ReplaceChangePlanPurchaseMode] === 'true') {
      return isChangePlan || purchaseMode === PurchaseMode.CHANGE_PLAN;
    }
    return purchaseMode === PurchaseMode.CHANGE_PLAN;
  }

  @computed
  get isReplaceChangePlanPurchaseModeEnabledAndIsChangePlan(): boolean {
    const { isChangePlan, experiments, purchaseMode } = this.appContext;
    return (
      experiments[Experiments.ReplaceChangePlanPurchaseMode] === 'true' &&
      (isChangePlan || purchaseMode === PurchaseMode.CHANGE_PLAN)
    );
  }

  @computed
  get isSwitchPlan(): boolean {
    return this.isChangePlan || this.isSecondUpgrade;
  }
  @computed
  get isFranchPackagePicker(): boolean {
    const { country } = this.appContext;
    return (
      this.experiments[Experiments.FranchPackagePicker] === 'true' &&
      country === Country.FR
    );
  }

  @computed
  get isJapanesePackagePicker(): boolean {
    const { country, locale } = this.appContext;
    return (
      this.experiments[Experiments.JapanesePackagePicker] === 'true' &&
      country === Country.JA &&
      locale === Language.JA
    );
  }

  getChangePlanProperties = (_products: AdvancedPlansProductData[]) =>
    _products.map(
      ({
        id,
        cycles,
        productCode,
        guid,
        productFamilyId,
      }): ChangePlanProductProperties => ({
        id,
        cycles,
        productCode,
        guid,
        productFamilyId,
      }),
    );

  private isSeasonalPurchaseMode(): boolean {
    return [
      PurchaseMode.SALE,
      PurchaseMode.SEASONAL_EXTENDED,
      PurchaseMode.LAST_DAY_SALE,
    ].includes(this.appContext.purchaseMode);
  }

  getDefaultProduct = () => {
    const { advancedPlansProducts } = this.appContext;

    return (
      advancedPlansProducts?.find((product) => {
        return product?.productFamily === ProductFamily.Core;
      }) ?? advancedPlansProducts[0]
    );
  };

  getSignInRedirectionLink = () => {
    const isNewStudioAnonymousContentEnabled =
      this.experiments[Experiments.NewStudioAnonymousContent] === 'true';
    if (this.isDayful) {
      return '//users.wix.com/dayful/signin?view=sign-up&sendEmail=true&loginCompName=Dayful%20Get%20Started&loginDialogContext=signup&enforceSso=true&autoRedirect=true&redirectTo=https%3A%2F%2Fwww.wix.com%2Fresolve-dayful-intro&skipSurvey=true';
    }
    if (this.isEditorX) {
      return `//users.editorx.com/signin?postLogin=https://manage.editorx.com/store/plans`;
    }
    if (this.shouldRenderWixStudioPackagePicker) {
      const { locale } = this.appContext;
      if (
        [
          Language.JA,
          Language.FR,
          Language.PT,
          Language.DE,
          Language.ES,
        ].includes(locale as Language)
      ) {
        return isNewStudioAnonymousContentEnabled
          ? `//${locale}.wix.com/signin?studio=true&loginDialogContext=signup&postLogin=https%3A%2F%2F${locale}.wix.com%2Fpremium-purchase-plan%2Fstudio&postSignUp=https:%2F%2Fwww.wix.com%2Fintro%2Fstudio%3Fref%3Dstudio_signup`
          : `//${locale}.wix.com/studio/waitlist`;
      }
      return isNewStudioAnonymousContentEnabled
        ? '//users.wix.com/signin?studio=true&loginDialogContext=signup&postLogin=https%3A%2F%2Fwww.wix.com%2Fpremium-purchase-plan%2Fstudio&postSignUp=https:%2F%2Fwww.wix.com%2Fintro%2Fstudio%3Fref%3Dstudio_signup'
        : '//wix.com/studio/waitlist';
    }
    return `//users.wix.com/signin?postLogin=https://www.wix.com/store/plans`;
  };

  @computed
  get currentPlanInOfferingMode(): CurrentPlanInOfferingMode {
    const { advancedPlansProducts, isDynamicOffering } = this.appContext;
    const productToCheck = this.getChangePlanProperties(advancedPlansProducts);
    const isChangePlanProductFamilyFixEnabled =
      this.experiments[Experiments.ChangePlanProductFamilyFix] === 'true';

    const { currentPlanDetails } = this.appContext;
    if ((!this.isChangePlan && !this.isSecondUpgrade) || !currentPlanDetails) {
      return CurrentPlanInOfferingMode.None;
    }
    const matchProduct = (product) => {
      const { guid, planId } = currentPlanDetails;
      if (isDynamicOffering) {
        return product.guid === guid;
      }
      return product.id === planId;
    };
    if (productToCheck.find(matchProduct)) {
      return CurrentPlanInOfferingMode.ProductFullMatch;
    }
    if (
      productToCheck.find(
        (p) =>
          p.productCode === currentPlanDetails.productCode ||
          (isChangePlanProductFamilyFixEnabled &&
            p.productFamilyId === currentPlanDetails.productFamilyId),
      )
    ) {
      return CurrentPlanInOfferingMode.ProductPartialMatch;
    }

    return CurrentPlanInOfferingMode.ProductNotMatch;
  }

  @computed
  get isChangePlanToastMode(): boolean {
    return (
      this.currentPlanInOfferingMode ===
      CurrentPlanInOfferingMode.ProductNotMatch ||
      this.currentPlanInOfferingMode ===
      CurrentPlanInOfferingMode.ProductPartialMatch
    );
  }

  // this function is throttled because when changing steps before exit animation is done
  // TransitionGroup will not remove exit animation class. there is an issue in github
  // about it: https://github.com/reactjs/react-transition-group/issues/337
  changeStep: (
    change: STEP_ACTIONS,
    actionReporter: ActionTypeReporter,
    specificStep?: number,
  ) => void = throttle(
    (
      change: STEP_ACTIONS,
      actionReporter: ActionTypeReporter,
      specificStep?: number,
    ) => {
      this.previousStep = this.currentStep;
      switch (change) {
        case STEP_ACTIONS.DECREMENT:
          {
            this.decrementStep();
          }
          break;
        case STEP_ACTIONS.INCREMENT:
          {
            this.incrementStep();
          }
          break;
        case STEP_ACTIONS.SPECIFIC:
          {
            if (specificStep === undefined) {
              SentryLogger.logError(
                new Error('Tried to set specific step without providing value'),
              );
              return;
            }
            this.setStep(specificStep);
          }
          break;
        default:
          return;
      }
      actionReporter(
        BiStepNames[this.previousStep],
        BiStepNames[this.currentStep],
      );
      this.scrollToTop();
    },
    this.changeStepThrottle,
  );

  @computed
  get getDefaultCurrentProductForPlanType() {
    // TODO: using the old appContext.products, remove this.
    const { advancedPlansProducts } = this.appContext;
    const _products: BaseProduct[] = advancedPlansProducts;
    const productsPerPlanType = _products.filter(
      (product) => product.type === this.planType,
    );
    return (
      productsPerPlanType.find((prod) => !!prod.hatKey) ||
      Utils.findProductByPartialName(productsPerPlanType, 'unlimited') ||
      Utils.findProductByPartialName(productsPerPlanType, 'grow') ||
      Utils.findProductByPartialName(productsPerPlanType, 'standard') ||
      productsPerPlanType[0] ||
      this.appContext.products[0]
    );
  }

  @computed
  get pricesIncludeTax() {
    const { country } = this.appContext;
    return taxIncludedCountries.includes(country);
  }

  get isUseDealerIntegrationIntentElementsEnabled(): boolean {
    return (
      this.experiments[Experiments.useDealerIntegrationIntentBasedElements] ===
      'true'
    );
  }

  get isEnterpriseTabEnabled(): boolean {
    return this.experiments[Experiments.AddEnterpriseTab] === 'true';
  }

  get isEditorXSpecialDiscountEnabled(): boolean {
    if (this.isReplaceChangePlanPurchaseModeEnabledAndIsChangePlan) {
      return false;
    }
    return (
      this.appContext.purchaseMode === PurchaseMode.SPECIAL_DISCOUNT &&
      this.isEditorX
    );
  }

  get isVoucherDisclaimerHidden(): boolean {
    const { hasGoogleVoucher } = this.appContext;
    return !hasGoogleVoucher;
  }

  get googleVoucherAmounts(): VoucherSettings {
    const voucherDefualts: VoucherSettings = {
      amountToSpend: 25,
      amount: 75,
      availableInUserCountry: true,
      currencyCode: 'USD',
      voucherType: VouchersType.GOOGLE_ADS,
    };
    const googleVoucher =
      this.appContext.voucherSettings?.find(
        (voucherSetting) => voucherSetting.voucherType === 'GOOGLE_ADS',
      ) || voucherDefualts;
    const { amountToSpend, amount, currencyCode } = googleVoucher;
    return {
      ...voucherDefualts,
      amount,
      amountToSpend,
      currencyCode,
    };
  }

  getLandingTab = () => {
    if (this.isDayful) {
      return PlanType.Website;
    }
    if (this.isBusinessTab) {
      return PlanType.Business;
    }

    if (
      this.experiments[Experiments.useTabDSModel] === 'true' &&
      this.appContext.isBusinessIntent !== undefined
    ) {
      return this.appContext.isBusinessIntent
        ? PlanType.Business
        : PlanType.Website;
    }
    const parsedDealerOffers = this.appContext.parsedDealerOffers;
    const landingTabOffer = (
      this.isSecondUpgrade
        ? parsedDealerOffers.get(RealEstateId.AppIntentElementsSecondUpgrade)
        : parsedDealerOffers.get(RealEstateId.AppIntentElementsInitialPurchase)
    ) as ParsedAppIntentElements;

    return this.isUseDealerIntegrationIntentElementsEnabled
      ? landingTabOffer.landingTab
      : PlanType.Website;
  };

  @computed
  get isSepaCountry() {
    return (
      this.experiments[Experiments.EnableSepaNoRefundDisclaimers] === 'true' &&
      SEPA_ENABLED_COUNTRIES.includes(this.appContext.country)
    );
  }

  @computed
  get isMediumSizeTableView() {
    return this.tableViewMode === TableViewMode.Medium;
  }

  @computed
  get isRedirectToCart() {
    return isRedirectToCartFlow(this.appContext);
  }

  @computed
  get appFlow(): AppFlow {
    return this.isRedirectToCart ? AppFlow.CART : AppFlow.CLASSIC;
  }

  get intent() {
    const { parsedDealerOffers, verticalName } = this.appContext;
    const intentOffer = (
      this.isSecondUpgrade
        ? parsedDealerOffers.get(RealEstateId.AppIntentElementsSecondUpgrade)
        : parsedDealerOffers.get(RealEstateId.AppIntentElementsInitialPurchase)
    ) as ParsedAppIntentElements;

    return this.isUseDealerIntegrationIntentElementsEnabled
      ? intentOffer.intent
      : verticalName;
  }

  get advancedPlansBusinessProducts(): AdvancedPlansProductData[] {
    const { advancedPlansProducts } = this.appContext;
    return advancedPlansProducts.filter(
      ({ type }) => type === PlanType.Business,
    );
  }

  get advancedPlansWebsiteProducts(): AdvancedPlansProductData[] {
    const { advancedPlansProducts } = this.appContext;
    return advancedPlansProducts.filter(
      ({ type }) => type === PlanType.Website,
    );
  }

  get defaultProductSelection(): AdvancedPlansProductData {
    const products =
      this.planType === PlanType.Website
        ? this.advancedPlansWebsiteProducts
        : this.advancedPlansBusinessProducts;
    let defualtProduct = products[0];
    products.forEach((product) => {
      const _product = new AdvancedPlansProductModel(product, this);
      defualtProduct = _product.isSelected() ? product : defualtProduct;
    });
    return defualtProduct;
  }

  get isSunriseV2Enabled(): boolean {
    return (
      !this.isEditorX &&
      !this.isDayful &&
      isSunriseExperimentsOn(this.experiments)
    );
  }
  get shouldRenderWixStudioPackagePicker(): boolean {
    return (
      this.appContext.packagePickerType === PackagePickerType.Studio ||
      this.appContext.packagePickerType === PackagePickerType.EditorXToStudio
    );
  }

  get shouldShowEditorXBanner(): boolean {
    return this.appContext.packagePickerType === PackagePickerType.EditorX;
  }

  get hasMonthlyCycle() {
    const currentProduct = this.appContext.advancedPlansProducts.find(
      (product) => product.type === this.planType,
    );
    return !!currentProduct?.cycles.some(
      (cycle) => cycle.cycleId === PaymentCycle.MONTHLY,
    );
  }

  get offeringHatData() {
    return this.appContext.parsedDealerOffers.get(
      RealEstateId.RecommendedHat,
    ) as RecommendedHatData;
  }

  get revShareData() {
    return this.appContext.revShareData;
  }

  get isEligibleForRevShare() {
    const { revShareData } = this.appContext;
    if (!revShareData) {
      return false;
    }
    const isStudioPackagePicker =
      this.appContext.packagePickerType === PackagePickerType.Studio ||
      this.appContext.packagePickerType === PackagePickerType.EditorXToStudio;
    if (!isStudioPackagePicker) {
      return false;
    }
    if (this.isInSale) {
      return false;
    }
    if (this.isChangePlan || this.appContext.isUpgrade) {
      return false;
    }
    return Object.values(revShareData).some(
      (productRevShare) =>
        Object.keys(productRevShare).length > 0 &&
        productRevShare.yearlyRevShareRate !== undefined,
    );
  }

  get isResellerOffering(): boolean {
    return this.appContext.resellerOfferingBrandingInfo !== undefined;
  }

  get resellerOfferingBrandingInfo(): AccountBrandingInfo | undefined {
    return this.appContext.resellerOfferingBrandingInfo;
  }

  showHatFromDealer = () => {
    return (
      this.experiments[Experiments.DynamicHat] === 'true' &&
      (!!this.offeringHatData?.productFamilies?.length ||
        !!this.offeringHatData?.productIds?.length)
    );
  };

  isCycleSelectorsInProductsPageEnabled(): boolean {
    return (
      this.experiments[Experiments.CycleSelectorsInProductsPage] === 'true' &&
      !this.appContext.currentPlanDetails?.guid
    );
  }

  get isWixelLightCouponAvailable(): boolean {
    if (!this.appContext.isWixelLightCouponAvailable) {
      return false;
    }

    const isSwitchPlan = this.isChangePlan || this.appContext.isUpgrade;
    const currentPlan = this.appContext.currentPlanDetails;
    const isLightMonthly = currentPlan?.productFamilyId === SUNRISE_FAMILY_LIGHT_ID && currentPlan.paymentCycle === PaymentCycle.MONTHLY;

    if (isSwitchPlan && !isLightMonthly) {
      return false;
    }

    return true;
  }

  @action.bound
  shouldShowWixelLightCoupon(productFamilyId: string): boolean {
    return this.appContext.isWixelLightCouponAvailable && SUNRISE_FAMILY_LIGHT_ID === productFamilyId;
  }
}
